import { useEffect } from 'react';
import {
  AssetManufacturerModel,
  AssetModel,
  CustomerModel,
  MineLocationModel,
  SiteModel,
} from '../data/types';
import useMergedState from './useMergedState';
import { inspectionDB } from '../data/database.structure';

interface CustomerSitesAssetsState {
  customers: CustomerModel[] | [];
  sites: SiteModel[] | [];
  manufacturers: AssetManufacturerModel[] | [];
  assets: AssetModel[] | [];
  mineLocations: MineLocationModel[] | [];
  isLoading: boolean;
  error?: string;
}

interface CustomerSitesAssetsProps {
  customerId?: number | null;
  siteId?: number | null;
}

export const useCustomerSitesAssets = ({
  customerId,
  siteId,
}: CustomerSitesAssetsProps) => {
  const [state, setState] = useMergedState<CustomerSitesAssetsState>({
    customers: [],
    sites: [],
    manufacturers: [],
    assets: [],
    mineLocations: [],
    isLoading: false,
    error: undefined,
  });

  const fetchCustomers = async () => {
    try {
      const customers = (await inspectionDB.customers.toArray()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      const sites = await inspectionDB.sites
        .where('customerId')
        .anyOf(customers.map((customer) => customer.id))
        .toArray();

      const assets = await inspectionDB.assets
        .where('siteId')
        .anyOf(sites.map((site) => site.id))
        .toArray();
      const mineLocations = await inspectionDB.mineLocations
        .where('siteId')
        .anyOf(sites.map((site) => site.id))
        .toArray();

      // Filter sites to only those that have assets or mine locations
      const filteredSites = sites.filter(
        (site) =>
          assets.some((asset) => asset.siteId === site.id) ||
          mineLocations.some((mineLocation) => mineLocation.siteId === site.id),
      );

      const customerIdsWithSites = new Set(
        filteredSites.map((site) => site.customerId),
      );
      setState({
        customers: customers.filter((customer) =>
          customerIdsWithSites.has(customer.id),
        ),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSites = async () => {
    try {
      if (!customerId) {
        return;
      }

      const sites = await inspectionDB.sites
        .where('customerId')
        .equals(customerId)
        .toArray();

      const assets = await inspectionDB.assets
        .where('siteId')
        .anyOf(sites.map((site) => site.id))
        .toArray();
      const mineLocations = await inspectionDB.mineLocations
        .where('siteId')
        .anyOf(sites.map((site) => site.id))
        .toArray();

      // Filter sites to only those that have assets or mine locations
      const filteredSites = sites.filter(
        (site) =>
          assets.some((asset) => asset.siteId === site.id) ||
          mineLocations.some((mineLocation) => mineLocation.siteId === site.id),
      );

      setState({
        sites: filteredSites as SiteModel[],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssets = async () => {
    try {
      if (!siteId) {
        return;
      }

      const assets = (
        await inspectionDB.assets.where('siteId').equals(siteId).toArray()
      ).sort((a, b) => a.name.localeCompare(b.name));
      const mineLocations = await inspectionDB.mineLocations
        .where('siteId')
        .equals(siteId)
        .toArray();

      setState({
        assets: assets as AssetModel[],
        mineLocations: mineLocations as MineLocationModel[],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchManufacturers = async () => {
    try {
      const manufacturers = await inspectionDB.assetManufacturers.toArray();
      setState({
        manufacturers,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchManufacturers();
  }, []);

  useEffect(() => {
    try {
      fetchSites();
    } catch (e) {
    } finally {
      setState({
        isLoading: false,
      });
    }
  }, [customerId]);

  useEffect(() => {
    fetchAssets();
  }, [siteId]);

  return { state };
};
